import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { commonEmployeeActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const employeeImportApi = createImportApi({
    actions: commonEmployeeActions,
    capabilities,
    endpoint: 'common/employees',
    templateFileName: 'EmployeeImportTpl'
});
export const ApiEmployeesImportCollection = employeeImportApi.importCollection;
export const ApiEmployeesDownloadImportModelItem = employeeImportApi.downloadImportModel;

export const ApiEmployeesGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .post('common/employees/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonEmployeeActions.setList(response?.data?.list));
                    dispatch(commonEmployeeActions.setCount(response?.data?.count));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('common/employees/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiEmployeesPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .post('common/employees', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonEmployeeActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .post('common/employees/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonEmployeeActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(commonEmployeeActions.startExporting());
    }

    await gestopAPI
        .post('common/employees/export', { exportType, format, columns, filters, orderBy, groupBy }, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(commonEmployeeActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(commonEmployeeActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .put(`common/employees/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonEmployeeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .patch(`common/employees/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonEmployeeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .post(`common/employees/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonEmployeeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .post(`common/employees/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonEmployeeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesExportItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(commonEmployeeActions.startExporting());
    }

    await gestopAPI
        .get(`common/employees/export/${id}`, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(commonEmployeeActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(commonEmployeeActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonEmployeeActions.startLoading());
    }

    await gestopAPI
        .get(`common/employees/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonEmployeeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonEmployeeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiEmployeesFileManagerUpload = async ({
    id,
    params = '',
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post(`common/employees/filemanager/upload/${id}?${params}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' } // Specific content type for file requests
        })
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};
